import { Alert, App as AntApp, DatePicker, Form, Modal, Select } from "antd";
import { useMutation, useQuery, useQueryClient } from "@tanstack/react-query";
import dayjs, { type Dayjs } from "dayjs";
import { useEffect, useState } from "react";
import { useTranslation } from "react-i18next";
import type { components } from "@qorakul/shared/api";
import { api } from "../app/api.ts";
import { ApiError, applyFieldErrors } from "../app/apiError.ts";
import { classKeys, defaultYear, formatDate, useAcademicYears } from "../app/classes.ts";

export type Placement = components["schemas"]["PlacementDetails"];

export const placementKeys = {
  history: (studentId: string) => ["students", studentId, "placements"] as const,
  rosters: ["students", "roster"] as const,
  roster: (classId: string, date: string) => ["students", "roster", classId, date] as const,
};

interface Props {
  studentId: string;
  /** The open placement, if any: a move closes it the day before the new class starts. */
  open: Placement | undefined;
  mode: "place" | "end" | undefined;
  onClose(): void;
}

/** Field names match the API (classId, from, to) so server field errors land under the right input. */
interface PlacementForm {
  academicYearId?: string;
  classId?: string;
  from?: Dayjs;
  to?: Dayjs;
}

/** Places or moves a student into a class from a date, or ends the open placement on a last day. */
export function PlacementModal({ studentId, open, mode, onClose }: Props) {
  const { t } = useTranslation();
  const { message } = AntApp.useApp();
  const queryClient = useQueryClient();
  const [form] = Form.useForm<PlacementForm>();
  const [summary, setSummary] = useState<string[]>([]);
  const years = useAcademicYears();
  const openYears = (years.data ?? []).filter((y) => y.status !== "Closed");
  const yearId = Form.useWatch("academicYearId", form);

  const classes = useQuery({
    queryKey: classKeys.list(yearId),
    enabled: mode === "place" && yearId !== undefined,
    queryFn: async () => {
      const { data, error } = await api.GET("/academic/classes", { params: { query: yearId ? { AcademicYearId: yearId } : {} } });
      if (error || !data) throw new Error(t("app.error"));
      return data;
    },
  });

  // Each opening starts from a clean form: today as the date, the open placement's year (or the active one) preselected.
  useEffect(() => {
    if (mode) {
      const candidates = (years.data ?? []).filter((y) => y.status !== "Closed");
      form.resetFields();
      const yearToShow = candidates.find((y) => y.id === open?.academicYearId) ?? defaultYear(candidates);
      form.setFieldsValue({ from: dayjs(), to: dayjs(), ...(yearToShow ? { academicYearId: yearToShow.id } : {}) });
      setSummary([]);
    }
  }, [mode, open, form, years.data]);

  const mutation = useMutation({
    mutationFn: async (values: PlacementForm) => {
      const path = { id: studentId };
      const result =
        mode === "end"
          ? await api.POST("/students/{id}/placements/end", { params: { path }, body: { to: values.to?.format("YYYY-MM-DD") ?? null } })
          : await api.POST("/students/{id}/placements", { params: { path }, body: { classId: values.classId ?? null, from: values.from?.format("YYYY-MM-DD") ?? null } });
      if (!result.data) throw new ApiError(result.response.status, result.error);
      return result.data;
    },
    onSuccess: async (history) => {
      queryClient.setQueryData(placementKeys.history(studentId), history);
      await Promise.all([
        queryClient.invalidateQueries({ queryKey: ["students", studentId], exact: true }),
        queryClient.invalidateQueries({ queryKey: placementKeys.rosters }),
        queryClient.invalidateQueries({ queryKey: ["audit"] }),
      ]);
      void message.success(mode === "end" ? t("placement.ended") : t("placement.placed"));
      onClose();
    },
    onError: (err) => setSummary(applyFieldErrors(form, err, t("app.error"))),
  });

  const required = [{ required: true, message: t("family.validation.required") }];
  const classOptions = (classes.data ?? []).map((c) => ({ value: c.id, label: `${c.name} · ${c.courseName}` }));
  return (
    <Modal
      open={mode !== undefined}
      title={mode === "end" ? t("placement.endTitle", { name: open?.className ?? "" }) : t("placement.placeTitle")}
      okText={t("family.save")}
      cancelText={t("family.cancel")}
      confirmLoading={mutation.isPending}
      onOk={() => form.submit()}
      onCancel={onClose}
      destroyOnHidden
    >
      {summary.length > 0 && <Alert type="error" showIcon role="alert" style={{ marginBottom: 16 }} title={summary.map((m) => <div key={m}>{m}</div>)} />}
      {mode === "place" && open && (
        <Alert type="info" showIcon style={{ marginBottom: 16 }} title={t("placement.moveHint", { name: open.className, since: formatDate(open.from) })} />
      )}
      {mode === "end" && <Alert type="info" showIcon style={{ marginBottom: 16 }} title={t("placement.endHint")} />}
      <Form<PlacementForm> form={form} layout="vertical" requiredMark={false} onFinish={(values) => mutation.mutate(values)}>
        {mode === "place" && (
          <>
            <Form.Item name="academicYearId" label={t("placement.year")} rules={required}>
              <Select
                loading={years.isPending}
                options={openYears.map((y) => ({ value: y.id, label: y.label }))}
                onChange={() => form.setFieldValue("classId", undefined)}
              />
            </Form.Item>
            <Form.Item
              name="classId"
              label={t("placement.class")}
              rules={required}
              extra={yearId && !classes.isPending && classOptions.length === 0 ? t("placement.noClasses") : undefined}
            >
              <Select options={classOptions} loading={classes.isFetching} showSearch={{ optionFilterProp: "label" }} />
            </Form.Item>
          </>
        )}
        <Form.Item name={mode === "end" ? "to" : "from"} label={mode === "end" ? t("placement.to") : t("placement.from")} rules={required}>
          <DatePicker style={{ width: "100%" }} format="DD.MM.YYYY" allowClear={false} />
        </Form.Item>
      </Form>
    </Modal>
  );
}
